import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api, type BezugTyp, type Kommentar } from '../api';
import { sync, darf } from '../sync';
import { useIch } from '../App';
import { useKommentare, kommentarEintragen, kommentarEntfernen } from '../kommentare';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { toast } from './toast';
import { frage } from './frage';
import { menueOeffnen, kopieren } from './Kontextmenue';
import { bereinigen } from '../kontextmenue';
import { usePersonen } from './SteuerungTeile';
import { ALLE, FARBEN, zerlegen, klartext, umschliessen, faerben, erwaehnungAn, erwaehnbare, erwaehnbareGruppen, erwaehnungEinsetzen, inGruppe, type Eingabe, type Knoten } from '../kommentartext';
import type { Person } from './SteuerungTeile';

const zeit = (iso: string) => new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });

/** Tooltip einer Gruppen-Erwähnung: wer damit gemeint ist. */
function gruppeTitel(gruppe: string, personen: Person[]): string {
  const l = inGruppe(personen, gruppe);
  return (gruppe === ALLE ? 'Alle Mitglieder des Projekts' : `Alle Mitglieder von ${gruppe}`) + (l.length ? ':\n' + l.map((p) => p.name || p.email).join('\n') : '');
}

/** Kommentartext mit Auszeichnung (fett, kursiv, Farbe) und hervorgehobenen Erwähnungen (Personen, @Alle, @Firma). */
function Formatiert({ knoten, personName, personen }: { knoten: Knoten[]; personName: (e: string) => string; personen: Person[] }): ReactNode {
  return knoten.map((k, i) => typeof k === 'string' ? k
    : k.art === 'erwaehnung' ? (k.gruppe
      ? <span key={i} className="erwaehnung gruppe" title={gruppeTitel(k.gruppe, personen)} data-testid="kommentar-erwaehnung-gruppe">@{k.gruppe}</span>
      : <span key={i} className="erwaehnung" title={k.email} data-testid="kommentar-erwaehnung">@{personName(k.email)}</span>)
    : k.art === 'fett' ? <strong key={i}><Formatiert knoten={k.kinder} personName={personName} personen={personen} /></strong>
    : k.art === 'farbe' ? <span key={i} className={'kt-' + k.farbe}><Formatiert knoten={k.kinder} personName={personName} personen={personen} /></span>
    : <em key={i}><Formatiert knoten={k.kinder} personName={personName} personen={personen} /></em>);
}

/**
 * Kommentarbereich im Detailbereich (Schritt, Prozess, Entscheidung). Lesen alle Mitglieder;
 * schreiben ab Mitwirkend; löschen eigene, Admins alle.
 */
export function Kommentare({ bezugTyp, bezugId }: { bezugTyp: BezugTyp; bezugId: string }) {
  const projektId = sync.projektId;
  const { ich } = useIch();
  const alle = useKommentare(projektId, bezugTyp, bezugId);
  const [text, setText] = useState('');
  const [sendet, setSendet] = useState(false);
  const [marke, setMarke] = useState(0);
  const [dran, setDran] = useState(0);
  const [zu, setZu] = useState(false);
  const feld = useRef<HTMLTextAreaElement>(null);
  const { personen, personName } = usePersonen(projektId, alle.map((k) => k.von));
  const darfSchreiben = darf('mitwirkend');
  const istAdmin = darf('admin') || !!ich?.superadmin;

  const senden = async () => {
    if (!text.trim() || sendet) return;
    setSendet(true);
    try {
      kommentarEintragen(await api.kommentieren(projektId, bezugTyp, bezugId, text));
      setText('');
      setMarke(0);
    } catch (e) {
      toast('Kommentar nicht gespeichert: ' + (e as Error).message, true);
    } finally {
      setSendet(false);
    }
  };
  const loeschen = async (k: Kommentar) => {
    if (!(await frage('Kommentar löschen?', { ja: 'Löschen', gefahr: true }))) return;
    try { await api.kommentarLoeschen(projektId, k.id); kommentarEntfernen(k.id); }
    catch (e) { toast((e as Error).message, true); }
  };
  // Erwähnung: nach „@“ zuerst die Gruppen (Alle, Firmen), dann die passenden Mitglieder vorschlagen (ohne mich selbst)
  const angefangen = zu ? null : erwaehnungAn(text, marke);
  const vorschlaege: (Person | string)[] = angefangen
    ? [...erwaehnbareGruppen(personen, angefangen.suche), ...erwaehnbare(personen, angefangen.suche, ich?.email)].slice(0, 10) : [];
  // Markierung nach dem Einsetzen: gleich nach dem Zeichnen, damit weiteres Tippen an der richtigen Stelle landet
  const markieren = useRef<Eingabe | null>(null);
  useLayoutEffect(() => {
    const e = markieren.current;
    if (!e || !feld.current) return;
    markieren.current = null;
    feld.current.focus();
    feld.current.setSelectionRange(e.start, e.ende);
  });
  const setzen = (e: Eingabe) => {
    markieren.current = e;
    setText(e.text);
    setMarke(e.ende);
  };
  const auswahl = (): Eingabe => ({ text, start: feld.current?.selectionStart ?? text.length, ende: feld.current?.selectionEnd ?? text.length });
  const erwaehnen = (i: number) => {
    if (angefangen && vorschlaege[i]) setzen(erwaehnungEinsetzen(text, angefangen.start, marke, vorschlaege[i], personen));
  };
  const atZeichen = () => {
    const a = auswahl();
    const vor = a.start > 0 && !/\s/.test(text[a.start - 1]) ? ' @' : '@';
    setZu(false);
    setDran(0);
    setzen({ text: text.slice(0, a.start) + vor + text.slice(a.ende), start: a.start + vor.length, ende: a.start + vor.length });
  };
  const taste = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (vorschlaege.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setDran((d) => (d + (e.key === 'ArrowDown' ? 1 : vorschlaege.length - 1)) % vorschlaege.length); return; }
      if ((e.key === 'Enter' && !e.ctrlKey && !e.metaKey) || e.key === 'Tab') { e.preventDefault(); erwaehnen(Math.min(dran, vorschlaege.length - 1)); return; }
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setZu(true); return; }
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void senden(); }
    else if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'b') { e.preventDefault(); setzen(umschliessen(auswahl(), '**', '**')); }
    else if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'i') { e.preventDefault(); setzen(umschliessen(auswahl(), '*', '*')); }
  };
  const knoten = zerlegen(text, personen);
  const mitAuszeichnung = knoten.some((k) => typeof k !== 'string');

  // Automatische Notizen (Terminänderung) dokumentieren etwas – sie lassen sich weder ändern noch löschen
  const darfLoeschen = (k: Kommentar) => !k.art && darfSchreiben && (k.von === ich?.email || istAdmin);
  const eintrag = (k: Kommentar) => (
    <div key={k.id} className={'kommentar' + (k.art ? ' notiz-' + k.art : '')} data-testid={k.art === 'termin' ? 'kommentar-termin' : 'kommentar'}
      onContextMenu={(e) => menueOeffnen(e, bereinigen([
        { text: 'Text kopieren', icon: 'copy', aktion: () => kopieren(klartext(k.text), 'Kopiert.') },
        '-',
        { text: 'Löschen', icon: 'trash', gefahr: true, aktion: () => loeschen(k), wenn: darfLoeschen(k) },
      ]))}>
      <div className="kopf">
        <Avatar email={k.von} name={k.vonName} size={20} />
        <strong title={k.von}>{k.vonName || k.von}</strong>
        <span className="hint">{zeit(k.am)}</span>
        {k.ausTicket && <span className="chip aus-ticket" title="Aus einer Ticketnotiz in checkITeasy übernommen" data-testid="kommentar-aus-ticket">aus Ticket</span>}
        {k.art === 'termin' && <span className="chip notiz" title="Automatisch festgehalten, als der Termin geändert wurde – lässt sich nicht ändern oder löschen"><Icon name="kalender" size={11} /> Termin geändert</span>}
        <span style={{ flex: 1 }} />
        {darfLoeschen(k) && (
          <button className="btn icon small" title="Kommentar löschen" onClick={() => void loeschen(k)} data-testid="kommentar-loeschen"><Icon name="trash" /></button>
        )}
      </div>
      <div className="text" data-testid="kommentar-inhalt"><Formatiert knoten={zerlegen(k.text, personen)} personName={personName} personen={personen} /></div>
    </div>
  );

  return (
    <section className="kommentare" data-testid={'kommentare-' + bezugTyp}>
      <h3>Kommentare{alle.length > 0 && <span className="badge-count" style={{ marginLeft: 6 }}>{alle.length}</span>}</h3>
      {alle.length === 0 && <p className="hint">Noch keine Kommentare.</p>}
      {alle.map(eintrag)}
      {darfSchreiben && (
        <div className="neu">
          {/* onMouseDown: Fokus und Markierung bleiben im Textfeld */}
          <div className="leiste" onMouseDown={(e) => e.preventDefault()}>
            <button type="button" className="btn icon small" title={'Fett (Strg+B)\nMarkierten Text fett setzen'} onClick={() => setzen(umschliessen(auswahl(), '**', '**'))} data-testid="kommentar-fett"><b>F</b></button>
            <button type="button" className="btn icon small" title={'Kursiv (Strg+I)\nMarkierten Text kursiv setzen'} onClick={() => setzen(umschliessen(auswahl(), '*', '*'))} data-testid="kommentar-kursiv"><i>K</i></button>
            <span className="farben">
              {FARBEN.map((f) => (
                <button key={f.id} type="button" className={'farbe kt-' + f.id} title={`${f.name}\nMarkierten Text einfärben`} aria-label={`Farbe ${f.name}`}
                  onClick={() => setzen(faerben(auswahl(), f.id))} data-testid={'kommentar-farbe-' + f.id} />
              ))}
            </span>
            <button type="button" className="btn icon small" title={'Erwähnen (@)\nPerson, @Alle oder @Firma – die Erwähnten werden über den Kommentar benachrichtigt'} onClick={atZeichen} data-testid="kommentar-at">@</button>
          </div>
          <div className="eingabe">
            <textarea ref={feld} value={text} onKeyDown={taste} placeholder="Kommentar, Rückfrage, Hinweis … (@ erwähnt jemanden, Strg+Enter sendet)" data-testid="kommentar-text"
              onChange={(e) => { setText(e.target.value); setMarke(e.target.selectionStart); setZu(false); setDran(0); }}
              onSelect={(e) => setMarke(e.currentTarget.selectionStart)} onBlur={() => setZu(true)} />
            {vorschlaege.length > 0 && (
              <ul className="wahl-liste" role="listbox" data-testid="kommentar-erwaehnen">
                {vorschlaege.map((p, i) => (
                  <li key={typeof p === 'string' ? '@' + p : p.email} role="option" aria-selected={false} className={i === Math.min(dran, vorschlaege.length - 1) ? 'dran' : ''}
                    onMouseDown={(e) => { e.preventDefault(); erwaehnen(i); }} onMouseMove={() => i !== dran && setDran(i)}>
                    {typeof p === 'string'
                      ? <><Icon name="users" size={16} /><strong>{p}</strong>&nbsp;<span className="hint">{p === ALLE ? 'alle Mitglieder des Projekts' : `alle von ${p}`} ({inGruppe(personen, p).length})</span></>
                      : <><Avatar email={p.email} name={p.name} size={18} />{p.name ? `${p.name} (${p.email})` : p.email}</>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {mitAuszeichnung && <div className="vorschau text" data-testid="kommentar-vorschau"><Formatiert knoten={knoten} personName={personName} personen={personen} /></div>}
          <button className="btn small primary" disabled={!text.trim() || sendet} onClick={() => void senden()} data-testid="kommentar-senden">Kommentieren</button>
        </div>
      )}
    </section>
  );
}
